//Built-in libraries

//Third Party Libraries
import * as Yup from "yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";

//Custom Components
import Button from "../../UI/Button";
import { HookFormProvider } from "../../UI/HookForm/Provider";
import { HookForm } from "../../UI/HookForm/Form";
import { HookInput } from "../../UI/HookForm/Form/Input";
import { HookReactSingleSelect } from "../../UI/HookForm/Form/ReactSelect";

//Services
import { notifyUser } from "../../../store/Toast";
import { postNewTitleRole } from "../../../api/Terminal/OrgTitle";
import { getAllRoles } from "../../../api/roles-and-permission";
import { sanitizeResponseError } from "../../../utils";

//Assets and Miscellaneous
import { ReactQueryKey } from "../../../constants/ReactQueryKeys";
import { IPostNewTitlePayload } from "../../../api/Terminal/OrgTitle/types";

interface INewTitleFormData {
  titleName: string;
  roleSlug: IRole;
}

const validationSchema = Yup.object({
  titleName: Yup.string()
    .required("Required")
    .min(2, "This must be greater than 2 characters")
    .max(50, "This must be less than 25 characters"),
  roleSlug: Yup.object({
    name: Yup.string(),
    slug: Yup.string(),
  }).test("roleSlug", "You must select a valid role", (val) => {
    return val.name !== undefined && val.slug !== undefined;
  }),
});

export default function NewTitleForm({ closeForm }: { closeForm: () => void }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const fetchRoles = useQuery([ReactQueryKey.GET_ROLES], () => getAllRoles());
  const clientRoles = fetchRoles?.data?.data;
  const addNewOrgTitle = useMutation(
    (data: IPostNewTitlePayload) => postNewTitleRole({ data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ReactQueryKey.GET_ORG_TITLES]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Organizational Title Successfully Added`,
            toastType: "success",
          })
        );
        closeForm();
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${resError}`,
            toastType: "failed",
          })
        );
      },
    }
  );

  const onSubmit = (formData: INewTitleFormData) => {
    addNewOrgTitle.mutate({
      titleName: formData.titleName.trim(),
      roleSlug: formData.roleSlug.slug,
    });
  };

  return (
    <>
      <HookFormProvider resolver={yupResolver(validationSchema)} mode="all">
        <HookForm onSubmit={onSubmit}>
          <h2 className="mt-0">New Title</h2>
          <div className="space-y-5 mb-5">
            <HookInput
              customClassName="col-span-6"
              id="titleName"
              name="titleName"
              type="text"
              label="Organizational Title"
            />
            <HookReactSingleSelect
              id="roleSlug"
              componentClassName="col-span-2"
              options={clientRoles}
              getOptionLabel={(option) => (option as IRole).name}
              getOptionValue={(option) => (option as IRole).slug}
              placeholder="Select Role"
              name="roleSlug"
              label="Role"
            />
          </div>
          <div className="flex gap-4 justify-end">
            <Button
              label="Cancel"
              btnStyle="btn-neutral"
              btnType="button"
              onClick={closeForm}
            />
            <Button
              label={`${addNewOrgTitle.isLoading ? "Please wait..." : "Add"}`}
              btnStyle="btn-primary"
              btnType="submit"
              disabled={addNewOrgTitle.isLoading}
            />
          </div>
        </HookForm>
      </HookFormProvider>
    </>
  );
}
